import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const money = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

const fmtDateTime = () =>
  new Date().toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function EventCashPage() {
  const nav = useNavigate();
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);

  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [cart, setCart] = useState([]);
  const [amountPaid, setAmountPaid] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingSale, setSavingSale] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadData = async () => {
    setErr("");
    try {
      const res = await authFetch(eventId ? `${API_URL}/api/event-cash/events/${eventId}/` : `${API_URL}/api/event-cash/active/`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore caricamento evento.");

      setEvent(data);
      setItems((data.items || []).filter((x) => x.active));

      const statsRes = await authFetch(eventId ? `${API_URL}/api/event-cash/stats/?event_id=${eventId}` : `${API_URL}/api/event-cash/stats/`);
      const statsData = await statsRes.json().catch(() => ({}));
      if (statsRes.ok) setStats(statsData);
    } catch (e) {
      setErr(e.message || "Errore caricamento Cassa Eventi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const addItem = async () => {
    const name = newName.trim();
    const price = String(newPrice || "").replace(",", ".").trim();

    if (!event?.id) {
      setErr("Evento non caricato.");
      return;
    }

    if (!name || !price || Number(price) <= 0) {
      setErr("Inserisci nome articolo e prezzo valido.");
      return;
    }

    setErr("");
    setMsg("");

    try {
      const res = await authFetch(`${API_URL}/api/event-cash/items/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: event.id,
          name,
          price,
          active: true,
          sort_order: items.length + 1,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore salvataggio articolo.");

      setNewName("");
      setNewPrice("");
      await loadData();
      setMsg("Articolo aggiunto al listino evento.");
    } catch (e) {
      setErr(e.message || "Errore aggiunta articolo.");
    }
  };

  const deleteItem = async (item) => {
    const ok = window.confirm(`Eliminare "${item.name}" dal listino evento?`);
    if (!ok) return;

    setErr("");
    setMsg("");

    try {
      const res = await authFetch(`${API_URL}/api/event-cash/items/${item.id}/`, {
        method: "DELETE",
      });

      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Errore eliminazione articolo.");
      }

      setCart((prev) => prev.filter((x) => x.id !== item.id));
      await loadData();
      setMsg("Articolo eliminato.");
    } catch (e) {
      setErr(e.message || "Errore eliminazione articolo.");
    }
  };

  const addToCart = (item) => {
    setCart((prev) => {
      const existing = prev.find((x) => x.id === item.id);
      if (existing) {
        return prev.map((x) =>
          x.id === item.id ? { ...x, qty: Number(x.qty || 1) + 1 } : x
        );
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const removeOne = (item) => {
    setCart((prev) =>
      prev
        .map((x) =>
          x.id === item.id ? { ...x, qty: Number(x.qty || 1) - 1 } : x
        )
        .filter((x) => Number(x.qty || 0) > 0)
    );
  };

  const total = useMemo(
    () =>
      cart.reduce(
        (sum, x) => sum + Number(x.price || 0) * Number(x.qty || 0),
        0
      ),
    [cart]
  );

  const amountPaidNumber = useMemo(
    () => Number(String(amountPaid || "0").replace(",", ".")) || 0,
    [amountPaid]
  );

  const changeAmount = useMemo(
    () => amountPaidNumber - total,
    [amountPaidNumber, total]
  );

  const receiptHtml = (sale) => {
    const lineRows = cart
      .map((x) => {
        const qty = Number(x.qty || 0);
        const lineTotal = Number(x.price || 0) * qty;
        return `
          <div class="line">
            <div class="qty">${qty}</div>
            <div class="desc">${x.name}</div>
            <div class="price">${money(lineTotal)}</div>
          </div>
        `;
      })
      .join("");

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Ricevuta Cassa Eventi</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  body {
    width: 72mm;
    font-family: "Courier New", monospace;
    font-size: 13px;
    color: #000;
    margin: 0;
  }
  .center { text-align: center; }
  .brand {
    font-size: 25px;
    font-weight: 900;
    letter-spacing: 1px;
    line-height: 1.05;
  }
  .event {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    margin-top: 4px;
  }
  .sep {
    border-top: 2px dashed #000;
    margin: 10px 0;
  }
  .solid {
    border-top: 3px solid #000;
    margin: 10px 0;
  }
  .meta {
    font-size: 12px;
    line-height: 1.4;
  }
  .header, .line {
    display: grid;
    grid-template-columns: 9mm 1fr 23mm;
    gap: 2mm;
    align-items: start;
  }
  .header {
    font-weight: 900;
    border-bottom: 2px solid #000;
    padding-bottom: 4px;
    margin-bottom: 4px;
  }
  .line {
    padding: 4px 0;
    border-bottom: 1px dotted #000;
  }
  .qty { font-weight: 900; }
  .desc { font-weight: 700; word-break: break-word; }
  .price { text-align: right; font-weight: 900; }
  .total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 24px;
    font-weight: 900;
    margin-top: 8px;
  }
  .thanks {
    font-size: 16px;
    font-weight: 900;
    margin-top: 14px;
  }
  .small {
    font-size: 11px;
  }
</style>
</head>
<body>
  <div class="center">
    <div>🍷 🍷</div>
    <div class="brand">DALLO ZIO ROBY</div>
    <div class="event">${event?.name || "CASSA EVENTI"}</div>
  </div>

  <div class="sep"></div>

  <div class="center meta">
    <strong>★ CASSA EVENTI ★</strong><br/>
    ${fmtDateTime()}<br/>
    Ricevuta n. ${String(sale?.receipt_number || "").padStart(5, "0")}
  </div>

  <div class="solid"></div>

  <div class="header">
    <div>QTA</div>
    <div>DESCRIZIONE</div>
    <div style="text-align:right;">PREZZO</div>
  </div>

  ${lineRows}

  <div class="solid"></div>

  <div class="total">
    <div>TOTALE</div>
    <div>${money(total)}</div>
  </div>

  <div class="sep"></div>

  ${amountPaidNumber > 0 ? `
    <div class="line">
      <div class="qty"></div>
      <div class="desc">PAGATO</div>
      <div class="price">${money(amountPaidNumber)}</div>
    </div>
    <div class="line">
      <div class="qty"></div>
      <div class="desc">RESTO</div>
      <div class="price">${money(Math.max(changeAmount, 0))}</div>
    </div>
    <div class="sep"></div>
  ` : ""}

  <div class="center thanks">GRAZIE E BUONA FESTA!</div>
  <div class="center small">Ricevuta non fiscale</div>
  <div class="center small">${event?.name || "Cassa Eventi"}</div>

  <script>
    window.onload = function() {
      setTimeout(function(){ window.print(); }, 300);
    };
  </script>
</body>
</html>`;
  };


  const summaryHtml = () => {
    const rows = (stats?.items || [])
      .map((x) => `
        <div class="line">
          <div class="desc">${x.name}</div>
          <div class="qty">${x.quantity}</div>
          <div class="price">${money(x.total)}</div>
        </div>
      `)
      .join("");

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Riepilogo Serata</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  body {
    width: 72mm;
    font-family: "Courier New", monospace;
    font-size: 13px;
    color: #000;
    margin: 0;
  }
  .center { text-align: center; }
  .brand {
    font-size: 25px;
    font-weight: 900;
    letter-spacing: 1px;
    line-height: 1.05;
  }
  .event {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    margin-top: 4px;
  }
  .sep {
    border-top: 2px dashed #000;
    margin: 10px 0;
  }
  .solid {
    border-top: 3px solid #000;
    margin: 10px 0;
  }
  .meta {
    font-size: 12px;
    line-height: 1.4;
  }
  .big-total {
    text-align: center;
    font-size: 24px;
    font-weight: 900;
    margin: 8px 0;
  }
  .header, .line {
    display: grid;
    grid-template-columns: 1fr 13mm 23mm;
    gap: 2mm;
    align-items: start;
  }
  .header {
    font-weight: 900;
    border-bottom: 2px solid #000;
    padding-bottom: 4px;
    margin-bottom: 4px;
  }
  .line {
    padding: 4px 0;
    border-bottom: 1px dotted #000;
  }
  .desc { font-weight: 700; word-break: break-word; }
  .qty { text-align: right; font-weight: 900; }
  .price { text-align: right; font-weight: 900; }
  .thanks {
    font-size: 16px;
    font-weight: 900;
    margin-top: 14px;
  }
  .small { font-size: 11px; }
</style>
</head>
<body>
  <div class="center">
    <div class="brand">DALLO ZIO ROBY</div>
    <div class="event">${event?.name || "CASSA EVENTI"}</div>
  </div>

  <div class="sep"></div>

  <div class="center meta">
    <strong>★ RIEPILOGO SERATA ★</strong><br/>
    ${fmtDateTime()}
  </div>

  <div class="solid"></div>

  <div class="center meta">INCASSO TOTALE</div>
  <div class="big-total">${money(stats?.total || 0)}</div>

  <div class="center meta">SCONTRINI EMESSI: <strong>${stats?.sales_count || 0}</strong></div>

  <div class="solid"></div>

  <div class="header">
    <div>ARTICOLO</div>
    <div>QTA</div>
    <div style="text-align:right;">TOTALE</div>
  </div>

  ${rows || '<div class="center meta">Nessuna vendita registrata.</div>'}

  <div class="solid"></div>

  <div class="center thanks">FINE SERATA</div>
  <div class="center small">Riepilogo interno non fiscale</div>
  <div class="center small">${event?.name || "Cassa Eventi"}</div>

  <script>
    window.onload = function() {
      setTimeout(function(){ window.print(); }, 300);
    };
  </script>
</body>
</html>`;
  };

  const printSummary = async () => {
    setErr("");
    setMsg("");

    try {
      const statsRes = await authFetch(eventId ? `${API_URL}/api/event-cash/stats/?event_id=${eventId}` : `${API_URL}/api/event-cash/stats/`);
      const freshStats = await statsRes.json().catch(() => ({}));
      if (!statsRes.ok) throw new Error(freshStats?.detail || "Errore caricamento riepilogo.");

      setStats(freshStats);

      const w = window.open("", "_blank", "width=420,height=700");
      if (w) {
        w.document.open();
        w.document.write(summaryHtml());
        w.document.close();
      } else {
        setErr("Popup bloccato: consenti i popup per stampare il riepilogo.");
        return;
      }

      setMsg("Riepilogo serata stampato. Usa Azzera serata quando vuoi chiudere.");
    } catch (e) {
      setErr(e.message || "Errore stampa riepilogo.");
    }
  };

  const resetEvening = async () => {
    const ok = window.confirm("Azzerare davvero incasso, scontrini e statistiche della serata?");
    if (!ok) return;

    setErr("");
    setMsg("");

    try {
      const resetRes = await authFetch(eventId ? `${API_URL}/api/event-cash/reset/?event_id=${eventId}` : `${API_URL}/api/event-cash/reset/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const resetData = await resetRes.json().catch(() => ({}));
      if (!resetRes.ok) throw new Error(resetData?.detail || "Errore azzeramento statistiche.");

      setCart([]);
      setAmountPaid("");
      await loadData();
      setMsg("Statistiche serata azzerate.");
    } catch (e) {
      setErr(e.message || "Errore azzeramento serata.");
    }
  };

  const confirmSale = async () => {
    if (!event?.id || cart.length === 0) return;

    setSavingSale(true);
    setErr("");
    setMsg("");

    try {
      const payload = {
        event: event.id,
        payment_method: "CONTANTI",
        lines: cart.map((x) => ({
          name: x.name,
          quantity: Number(x.qty || 0),
          unit_price: x.price,
        })),
      };

      const res = await authFetch(`${API_URL}/api/event-cash/sales/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore salvataggio vendita.");

      const w = window.open("", "_blank", "width=420,height=700");
      if (w) {
        w.document.open();
        w.document.write(receiptHtml(data));
        w.document.close();
      } else {
        setErr("Popup bloccato: consenti i popup per stampare la ricevuta.");
      }

      setCart([]);
      setAmountPaid("");
      await loadData();
      setMsg(`Vendita salvata. Ricevuta n. ${data.receipt_number}.`);
    } catch (e) {
      setErr(e.message || "Errore conferma vendita.");
    } finally {
      setSavingSale(false);
    }
  };

  if (loading) {
    return <div className="container py-4">Caricamento Cassa Eventi...</div>;
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-muted small text-uppercase fw-semibold">
            Cassa Eventi
          </div>
          <h1 className="fw-bold mb-1">🎪 {event?.name || "Cassa Eventi"}</h1>
          <div className="text-muted">
            Listino ad hoc per la festa, cassa touch e ricevuta non fiscale.
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-4"
          onClick={() => nav("/gestione-app-clienti")}
        >
          ← Gestione App Clienti
        </button>
      </div>

      {err && <div className="alert alert-danger rounded-4">{err}</div>}
      {msg && <div className="alert alert-success rounded-4">{msg}</div>}

      <div className="row g-3">
        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow rounded-4 h-100">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">🍷 Listino evento</h4>

              <div className="row g-2 mb-3">
                <div className="col-12">
                  <input
                    className="form-control"
                    placeholder="Nome articolo, es. Bicchiere vino"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div className="col-8">
                  <input
                    className="form-control"
                    inputMode="decimal"
                    placeholder="Prezzo, es. 2.00"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                  />
                </div>
                <div className="col-4 d-grid">
                  <button className="btn btn-success" onClick={addItem}>
                    Aggiungi
                  </button>
                </div>
              </div>

              <div className="list-group">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="list-group-item d-flex justify-content-between align-items-center gap-2"
                  >
                    <div>
                      <div className="fw-bold">{item.name}</div>
                      <div className="text-muted small">{money(item.price)}</div>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteItem(item)}
                    >
                      Elimina
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="card border-0 shadow rounded-4 h-100">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">🧾 Cassa touch</h4>

              <div className="row g-3">
                {items.map((item) => (
                  <div className="col-6 col-md-4" key={item.id}>
                    <button
                      type="button"
                      className="btn btn-lg w-100 rounded-4 fw-bold"
                      onClick={() => addToCart(item)}
                      style={{
                        minHeight: 96,
                        background: "#5b0f1b",
                        color: "#fff",
                      }}
                    >
                      <div>{item.name}</div>
                      <div className="small opacity-75">{money(item.price)}</div>
                    </button>
                  </div>
                ))}
              </div>

              {stats && (
                <div className="alert alert-info rounded-4 mt-4 mb-0">
                  <div className="fw-bold">Riepilogo evento</div>
                  Scontrini: {stats.sales_count || 0} · Incasso: {money(stats.total || 0)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-3">
          <div className="card border-0 shadow rounded-4 h-100">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">🛒 Conto</h4>

              {cart.length === 0 ? (
                <div className="alert alert-info rounded-4">
                  Nessun articolo selezionato.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="d-flex justify-content-between align-items-center border-bottom pb-2"
                    >
                      <div>
                        <div className="fw-bold">
                          {item.qty} x {item.name}
                        </div>
                        <div className="text-muted small">
                          {money(Number(item.price) * Number(item.qty))}
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => removeOne(item)}
                      >
                        -
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-3 border-top">
                <div className="text-muted">Totale</div>
                <div className="display-6 fw-bold" style={{ color: "#5b0f1b" }}>
                  {money(total)}
                </div>
              </div>

                <div className="mt-3 p-3 border rounded-4 bg-light">
                  <label className="form-label fw-semibold">Cliente paga</label>
                  <input
                    className="form-control form-control-lg"
                    inputMode="decimal"
                    placeholder="Es. 50"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                  />

                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {[5, 10, 20, 50, 100].map((v) => (
                      <button
                        key={v}
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setAmountPaid(String(v))}
                      >
                        {v} €
                      </button>
                    ))}
                  </div>

                  {amountPaidNumber > 0 && (
                    <div className="mt-3">
                      {changeAmount >= 0 ? (
                        <>
                          <div className="text-muted">Resto da dare</div>
                          <div className="h3 fw-bold mb-0 text-success">
                            {money(changeAmount)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-muted">Mancano ancora</div>
                          <div className="h4 fw-bold mb-0 text-danger">
                            {money(Math.abs(changeAmount))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

              <div className="d-grid gap-2 mt-3">
                <button className="btn btn-outline-danger" onClick={() => { setCart([]); setAmountPaid(""); }}>
                  Svuota conto
                </button>
                <button
                  className="btn btn-success"
                  disabled={!cart.length || savingSale}
                  onClick={confirmSale}
                >
                  {savingSale ? "Salvataggio..." : "Conferma / Stampa"}
                </button>
                <button
                  className="btn btn-outline-primary"
                  disabled={!stats}
                  onClick={printSummary}
                >
                  📊 Stampa riepilogo serata
                </button>
                  <button
                    className="btn btn-outline-danger"
                    disabled={!stats || Number(stats?.sales_count || 0) === 0}
                    onClick={resetEvening}
                  >
                    🧹 Azzera serata
                  </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {stats?.items?.length > 0 && (
        <div className="card border-0 shadow rounded-4 mt-4">
          <div className="card-body p-4">
            <h4 className="fw-bold mb-3">📊 Statistiche venduto</h4>
            <div className="row g-2">
              {stats.items.map((x) => (
                <div className="col-12 col-md-4" key={x.name}>
                  <div className="border rounded-4 p-3 h-100">
                    <div className="fw-bold">{x.name}</div>
                    <div>Quantità: {x.quantity}</div>
                    <div>Totale: {money(x.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
